import { Component, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { InsuredDTO, INSURED_RELATIONSHIP_OPTIONS, normalizeRelationship } from '../../../model/InsuredDTO';
import { InsuredServiceService } from '../../../../services/insured-service.service';
import { InsuredModalComponent } from '../../../../modal/insured-modal/insured-modal.component';
import { PolicyService } from '../../../../services/policy-service';

@Component({
    selector: 'app-profile-insured',
    templateUrl: './profile-insured.component.html',
    styleUrls: ['./profile-insured.component.scss']
})
export class ProfileInsuredComponent implements OnInit {

    @ViewChild(InsuredModalComponent) insuredModal!: InsuredModalComponent;

    loading = false;
    insureds: any[] = [];
    // Pólizas DJ29 del usuario, para el selector de póliza del modal de alta.
    policies: Array<{ sequence: number; label: string }> = [];
    modalOpen = false;
    editingInsured: any = null;

    // Estado del diálogo de confirmación de baja de asegurado.
    confirmDeleteOpen = false;
    insuredToDelete: any = null;
    deleting = false;

    constructor(
        private insuredService: InsuredServiceService,
        private policyService: PolicyService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        this.loadPolicies();
        this.load();
    }

    // Carga las pólizas DJ29 (Asistencia Jurídica Familiar) del usuario para el selector del
    // modal de alta, mediante /policies/search filtrando por producto. Son las pólizas sobre
    // las que se pueden dar de alta asegurados.
    private loadPolicies(): void {
        this.policyService.getLegalAssistancePolicies()
            .then((list: any[]) => {
                this.policies = (list || [])
                    .filter(p => p && p.number !== null && p.number !== undefined)
                    .map(p => ({ sequence: Number(p.number), label: this.policyLabel(p) }));
            })
            .catch(() => {
                this.policies = [];
            });
    }

    private policyLabel(policy: any): string {
        const number = policy.number;
        return policy.product ? `${number} - ${policy.product}` : `${number}`;
    }

    private load(): void {
        this.loading = true;
        this.insuredService.getAll()
            .then((result: any) => {
                this.loading = false;
                this.insureds = this.mapInsureds((result && result.response) || []);
            })
            .catch(() => {
                this.loading = false;
                this.toastr.error('No se han podido cargar los asegurados.', 'Error de conectividad', { timeOut: 5000 });
            });
    }

    // El listado de Innova no devuelve un nombre completo, así que se calcula aquí para
    // mostrarlo en la tarjeta. La relación de parentesco (TITURELA) sí llega y se propaga
    // con el spread (incluido policySequence, la póliza a la que pertenece el asegurado). La
    // fecha de nacimiento llega en formato ISO (yyyy-MM-dd) y se muestra como dd/mm/yyyy.
    private mapInsureds(items: any[]): any[] {
        return items.map(i => ({
            ...i,
            fullName: [i.name, i.firstSurname, i.lastName].filter(part => !!part).join(' '),
            bornDate: this.toDisplayDate(i.bornDate)
        }));
    }

    /** Traduce el código de relación de Innova (TITURELA) a su etiqueta legible. */
    relationshipLabel(code: string): string {
        const value = normalizeRelationship(code);
        const option = INSURED_RELATIONSHIP_OPTIONS.find(o => o.value === value);
        return option ? option.label : (code ? code.trim() : '');
    }

    private toDisplayDate(isoDate: string): string {
        if (!isoDate) { return ''; }
        const [year, month, day] = isoDate.split('-');
        return `${day}/${month}/${year}`;
    }

    openModal(): void {
        this.editingInsured = null;
        this.modalOpen = true;
    }

    openEditModal(insured: any): void {
        this.editingInsured = insured;
        this.modalOpen = true;
    }

    closeModal(): void {
        this.modalOpen = false;
        this.editingInsured = null;
    }

    onSave(event: { dto: InsuredDTO; policySequence: number }): void {
        const isEditMode = !!this.editingInsured;
        // En modificación la póliza es la del asegurado (no cambia); en alta, la seleccionada
        // en el modal.
        const policySequence = isEditMode ? this.editingInsured.policySequence : event.policySequence;
        const request = isEditMode
            ? this.insuredService.modify(policySequence, event.dto)
            : this.insuredService.create(policySequence, event.dto);

        request
            .then(() => {
                this.modalOpen = false;
                this.editingInsured = null;
                this.load();
            })
            .catch(() => {
                if (this.insuredModal) { this.insuredModal.setRunning(false); }
                const message = isEditMode
                    ? 'No se ha podido modificar el asegurado.'
                    : 'No se ha podido añadir el asegurado.';
                this.toastr.error(message, 'Error de conectividad', { timeOut: 5000 });
            });
    }

    /** Abre el diálogo de confirmación de baja del asegurado seleccionado. */
    remove(insured: any): void {
        this.insuredToDelete = insured;
        this.confirmDeleteOpen = true;
    }

    /** Cierra el diálogo de confirmación sin eliminar. */
    cancelDelete(): void {
        if (this.deleting) { return; }
        this.confirmDeleteOpen = false;
        this.insuredToDelete = null;
    }

    /** Confirma la baja: llama al servicio con la póliza del asegurado, recarga y notifica. */
    confirmDelete(): void {
        if (this.deleting || !this.insuredToDelete) { return; }
        this.deleting = true;
        this.insuredService.delete(this.insuredToDelete.policySequence, this.insuredToDelete.documentNumber)
            .then(() => {
                this.deleting = false;
                this.confirmDeleteOpen = false;
                this.insuredToDelete = null;
                this.toastr.success('Asegurado eliminado correctamente.', undefined, { timeOut: 5000 });
                this.load();
            })
            .catch(() => {
                this.deleting = false;
                this.toastr.error('No se ha podido eliminar el asegurado.', 'Error de conectividad', { timeOut: 5000 });
            });
    }

    // El backend devuelve el número y el prefijo por separado; aquí se recomponen para mostrarlos
    // en el mismo formato que usa el resto de la aplicación: "+34 666999888".
    formatPhone(insured: any): string {
        if (!insured.mobilePhone) { return '—'; }
        if (!insured.mobilePhonePrefix) { return insured.mobilePhone; }
        return `+${insured.mobilePhonePrefix} ${insured.mobilePhone}`;
    }

    // Un mismo documento puede estar asegurado en varias pólizas DJ29, así que la clave de
    // seguimiento combina la póliza y el documento para que sea única.
    trackById(_index: number, insured: any): any {
        return `${insured.policySequence || ''}-${insured.documentNumber}`;
    }
}
